import * as Calendar from 'expo-calendar';
import type {
  CalendarEvent,
  CalendarTransparency,
  CalendarVisibility,
  CreateCalendarEventInput,
  EventParticipant,
} from '@/types/events';
import { RecurrenceRuleMapper } from './RecurrenceRuleMapper';
import { EventDescriptionSectionParser } from '@/lib/utils/EventDescriptionSectionParser';

/**
 * CalendarEventMapper transforms between expo-calendar events
 * and the app's CalendarEvent type
 */
export class CalendarEventMapper {
  /**
   * Map expo-calendar Event to CalendarEvent
   */
  static mapExpoEventToCalendarEvent(expoEvent: Calendar.Event): CalendarEvent {
    // Check if this is an all-day event
    const isAllDay = expoEvent.allDay === true;

    const startDate = this.extractDate(expoEvent.startDate);
    const startTime = isAllDay ? null : this.extractTime(expoEvent.startDate);
    const endDate = this.extractDate(expoEvent.endDate);
    const endTime = isAllDay ? null : this.extractTime(expoEvent.endDate);

    // Map expo-calendar availability to transparency
    const transparency: CalendarTransparency =
      expoEvent.availability === Calendar.Availability.FREE ? 'transparent' : 'opaque';

    // Map expo-calendar accessLevel to visibility
    const visibility: CalendarVisibility = this.mapAccessLevelToVisibility(expoEvent.accessLevel);

    // Parse description to extract family member IDs (attendees resolved separately via AttendeeResolver)
    const parsedDescription = EventDescriptionSectionParser.parse(expoEvent.notes);

    // Build participants from family labels only — attendees are lazy-loaded via AttendeeResolver
    const participants: EventParticipant[] = [];

    parsedDescription.familyMemberIds.forEach(familyId => {
      participants.push({
        id: `label-${familyId}`,
        role: 'label',
        familyMemberId: familyId,
      });
    });

    // Map organizer - iOS uses organizer object, Android uses organizerEmail string
    let organizer: { email: string; name?: string | null } | null = null;
    if (expoEvent.organizer) {
      const organizerEmail = (expoEvent.organizer as unknown as { email?: string }).email;
      organizer = {
        email: organizerEmail ?? '',
        name: expoEvent.organizer.name ?? null,
      };
    } else if (expoEvent.organizerEmail) {
      organizer = {
        email: expoEvent.organizerEmail,
        name: null,
      };
    }

    // Map recurrence rule if present
    const recurrence = RecurrenceRuleMapper.mapFromExpo(expoEvent.recurrenceRule);

    // Convert native alarms to reminders
    const eventStartDate = new Date(expoEvent.startDate);
    const reminders = expoEvent.alarms?.map((alarm, index) => {
      const offsetMs = (alarm.relativeOffset ?? 0) * 60 * 1000;
      const reminderTime = new Date(eventStartDate.getTime() + offsetMs);
      return {
        id: `alarm-${index}`,
        date: this.extractDate(reminderTime),
        time: this.extractTime(reminderTime) ?? '09:00',
      };
    }) ?? [];

    return {
      id: expoEvent.id,
      title: expoEvent.title || 'Untitled Event',
      startDate,
      startTime: startTime ?? undefined,
      endDate: endDate ?? undefined,
      endTime: endTime ?? undefined,
      allDay: isAllDay,
      description: expoEvent.notes ?? null,
      location: expoEvent.location ?? null,
      transparency,
      visibility,
      recurrence,
      organizer: organizer && organizer.email ? organizer : null,
      createdAt: this.toIsoString(expoEvent.creationDate),
      updatedAt: this.toIsoString(expoEvent.lastModifiedDate),
      deviceCalendarId: expoEvent.calendarId ?? '',
      extendedProperties: {
        participants: participants.length > 0 ? participants : undefined,
        reminders: reminders.length > 0 ? reminders : undefined,
      },
    };
  }

  /**
   * Map CalendarEvent to expo-calendar Event creation input
   */
  static mapCalendarEventToExpoEvent(
    event: CreateCalendarEventInput,
    calendarId: string,
  ): Omit<Calendar.Event, 'id' | 'creationDate' | 'lastModifiedDate'> {
    const startDate = this.createDateTime(event.startDate, event.startTime);
    const endDate = this.createDateTime(
      event.endDate ?? event.startDate,
      event.endTime ?? event.startTime,
    );

    // If endDate equals startDate and it's an all-day event, add 1 day to endDate
    const isAllDay = !event.startTime && !event.endTime;
    const finalEndDate = isAllDay && endDate <= startDate
      ? new Date(startDate.getTime() + 24 * 60 * 60 * 1000)
      : endDate;

    const availability = event.transparency === 'transparent'
      ? Calendar.Availability.FREE
      : Calendar.Availability.BUSY;

    const accessLevel = this.mapVisibilityToAccessLevel(event.visibility ?? 'default');

    // Map recurrence rule if present
    const recurrenceRule = event.recurrence
      ? RecurrenceRuleMapper.mapToExpo(event.recurrence)
      : undefined;

    // Map participants to attendees (only 'invited' role - NOT organiser)
    // The organiser should not be added as an attendee to prevent creating invites
    // No invites are created by default - only explicitly added invitees become attendees
    const attendees = event.extendedProperties?.participants
      ?.filter(p => p.role === 'invited')
      ?.map(p => ({
        email: p.email!,
        name: p.name ?? undefined,
        role: Calendar.AttendeeRole.OPTIONAL,
        status: Calendar.AttendeeStatus.INVITED,
        type: Calendar.AttendeeType.PERSON,
      }))
      ?.filter(a => a.email) ?? [];

    // Convert reminders to native alarms
    // CRITICAL: Explicitly set alarms to empty array if no reminders to prevent defaults
    const alarms = event.extendedProperties?.reminders && event.extendedProperties.reminders.length > 0
      ? event.extendedProperties.reminders.map(reminder => {
          const reminderDateTime = this.createDateTime(reminder.date, reminder.time);
          const relativeOffset = Math.round((reminderDateTime.getTime() - startDate.getTime()) / 60000);
          return { relativeOffset };
        })
      : []; // Empty array = no alarms (prevents device defaults)

    // IMPORTANT: only include `recurrenceRule` when it has a value.
    // Passing `recurrenceRule: null` makes expo-calendar's Android native code
    // (CalendarModule.kt) enter the recurrence branch via `containsKey` and
    // NPE on `getArguments("recurrenceRule").containsKey("frequency")`, which
    // escapes the JS try/catch under New Architecture and crashes the app.
    return {
      title: event.title,
      startDate,
      endDate: finalEndDate,
      allDay: isAllDay,
      notes: event.description ?? undefined,
      location: event.location ?? undefined,
      availability,
      accessLevel,
      ...(recurrenceRule ? { recurrenceRule } : {}),
      attendees: attendees.length > 0 ? attendees : undefined,
      calendarId,
      alarms: alarms.length > 0 ? alarms : [],
    } as unknown as Omit<Calendar.Event, 'id' | 'creationDate' | 'lastModifiedDate'>;
  }

  /**
   * Extract date string (YYYY-MM-DD) from Date or ISO string
   */
  private static extractDate(date: string | Date | null | undefined): string {
    if (!date) return new Date().toISOString().split('T')[0];

    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Extract time string (HH:mm) from Date or ISO string.
   * Returns null for all-day events
   */
  private static extractTime(date: string | Date | null | undefined): string | null {
    if (!date) return null;

    const d = new Date(date);
    // Check if this looks like an all-day event (time is 00:00)
    if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) {
      return null;
    }

    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  /**
   * Create Date object from date string and optional time string
   */
  private static createDateTime(dateStr: string, timeStr?: string | null): Date {
    const [year, month, day] = dateStr.split('-').map(Number);

    if (!timeStr) {
      // All-day event - use noon (12:00) to avoid timezone edge cases
      // Using midnight (00:00) can cause date shifts due to timezone conversions
      return new Date(year, month - 1, day, 12, 0, 0, 0);
    }

    // Timed event - parse date and time in local timezone
    const [hours, minutes] = timeStr.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes, 0, 0);
  }

  /**
   * Convert a Date or ISO string into an ISO string, falling back to now.
   */
  private static toIsoString(value: string | Date | null | undefined): string {
    if (!value) return new Date().toISOString();
    return value instanceof Date ? value.toISOString() : new Date(value).toISOString();
  }

  /**
   * Map expo-calendar EventAccessLevel to CalendarVisibility
   */
  private static mapAccessLevelToVisibility(
    accessLevel: Calendar.EventAccessLevel | null | undefined,
  ): CalendarVisibility {
    switch (accessLevel) {
      case Calendar.EventAccessLevel.PUBLIC:
        return 'public';
      case Calendar.EventAccessLevel.PRIVATE:
        return 'private';
      case Calendar.EventAccessLevel.CONFIDENTIAL:
        return 'confidential';
      default:
        return 'default';
    }
  }

  /**
   * Map CalendarVisibility to expo-calendar EventAccessLevel
   */
  private static mapVisibilityToAccessLevel(
    visibility: CalendarVisibility,
  ): Calendar.EventAccessLevel {
    switch (visibility) {
      case 'public':
        return Calendar.EventAccessLevel.PUBLIC;
      case 'private':
        return Calendar.EventAccessLevel.PRIVATE;
      case 'confidential':
        return Calendar.EventAccessLevel.CONFIDENTIAL;
      default:
        return Calendar.EventAccessLevel.DEFAULT;
    }
  }

  /**
   * Map expo-calendar AttendeeStatus to response status
   */

}
